//@flow
import React, { useEffect } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Translate } from "@quantum/i18n";
import moment from "moment";
import { range } from "ramda";

import {
  type Id,
  type MedidaSelecionada,
  type ErrosSelecao,
  type MesEAnoComposto,
  type EdicaoMedida,
  type ChaveEnum,
  ID_AJUSTE_DIAS_NORMAL,
  ID_PERIODO_MES_A_MES,
  ID_PERIODO_MES_A_MES_MENSAL,
  FORMATO_DATA_MES_ANO_COMPOSTA,
  ID_AJUSTE_DIAS_JANELA,
  ID_PERIODICIDADE_MENSAL,
  MODO_EXIBICAO_PONTUAL_MENSAL,
} from "../../../../../model";
import {
  type State,
  type ActionCreator,
  type ProcessadorPeriodo,
  i18n,
  getPeriodoUnicoSelecionado,
  getDataInicialCompostaSelecionada,
  getDataFinalCompostaSelecionada,
  selecionarAjusteDias,
  selecionarDataInicialComposta,
  selecionarDataFinalComposta,
  selecionarPrimeiraDataInicialComposta,
  selecionarPrimeiraDataFinalComposta,
  getEdicaoMedida,
  getMesesEAnosCompostos,
  getAjusteDiasSelecionado,
  getModoExibicaoMedidaSelecionada,
  getPeriodicidadeSelecionada,
} from "../../../../../state";
import { randomId } from "../../../../../util";
import SelecaoMesAnoComposto from "./SelecaoMesAnoComposto";

type Props = {
  exibir: boolean,
  dataInicialCompostaSelecionada: string,
  dataFinalCompostaSelecionada: string,
  ajusteDiasSelecionado: ?Id,
  selecionarDataInicialComposta: ActionCreator,
  selecionarDataFinalComposta: ActionCreator,
  selecionarPrimeiraDataInicialComposta: ActionCreator,
  selecionarPrimeiraDataFinalComposta: ActionCreator,
};

const exibirSelecaoMesAMes: (state: State) => boolean = (state: State) => {
  const periodoUnicoSelecionado: ?number = getPeriodoUnicoSelecionado(state);
  return (
    periodoUnicoSelecionado === ID_PERIODO_MES_A_MES ||
    periodoUnicoSelecionado === ID_PERIODO_MES_A_MES_MENSAL
  );
};

const useAutoTrocarAjusteDias = exibir => {
  const dispatch = useDispatch();

  const mesMaisRecente = useSelector(getMesesEAnosCompostos)[0];
  const mesFinalSelecionado = useSelector(getDataFinalCompostaSelecionada);

  useEffect(() => {
    if (exibir) {
      if (
        mesMaisRecente &&
        mesFinalSelecionado &&
        mesMaisRecente.nome !== mesFinalSelecionado
      ) {
        dispatch(selecionarAjusteDias(ID_AJUSTE_DIAS_NORMAL));
      }
    }
  }, [dispatch, exibir, mesMaisRecente, mesFinalSelecionado]);
};

function SelecaoMesAMes({
  exibir,
  dataInicialCompostaSelecionada,
  dataFinalCompostaSelecionada,
  ajusteDiasSelecionado,
  selecionarDataInicialComposta,
  selecionarDataFinalComposta,
  selecionarPrimeiraDataInicialComposta,
  selecionarPrimeiraDataFinalComposta,
}: Props) {
  useAutoTrocarAjusteDias(exibir);

  useEffect(() => {
    if (exibir && !dataInicialCompostaSelecionada) {
      selecionarPrimeiraDataInicialComposta();
    }
    if (exibir && !dataFinalCompostaSelecionada) {
      selecionarPrimeiraDataFinalComposta();
    }
  }, [
    exibir,
    dataInicialCompostaSelecionada,
    dataFinalCompostaSelecionada,
    selecionarPrimeiraDataInicialComposta,
    selecionarPrimeiraDataFinalComposta,
  ]);

  if (!exibir) {
    return null;
  }

  const handleDataInicialCompostaSelecionada = function(dataInicial) {
    selecionarDataInicialComposta(dataInicial);
  };

  const handleDataFinalCompostaSelecionada = function(dataFinal) {
    selecionarDataFinalComposta(dataFinal);
  };

  return (
    <div className="ConfiguracaoMedida">
      <div className="ConfiguracaoMedida__box">
        <label className="ConfiguracaoMedida__label">
          <Translate>maiusculas.dataInicial</Translate>
        </label>
        <SelecaoMesAnoComposto
          mesEAnoCompostoSelecionado={dataInicialCompostaSelecionada}
          onChange={handleDataInicialCompostaSelecionada}
        />
      </div>

      <div className="ConfiguracaoMedida__box">
        <label className="ConfiguracaoMedida__label">
          <Translate>maiusculas.dataFinal</Translate>
        </label>
        <SelecaoMesAnoComposto
          mesEAnoCompostoSelecionado={dataFinalCompostaSelecionada}
          onChange={handleDataFinalCompostaSelecionada}
          disabled={ajusteDiasSelecionado === ID_AJUSTE_DIAS_JANELA}
        />
      </div>
    </div>
  );
}

export function validacaoSelecaoMesAMes(state: State): ?ErrosSelecao {
  const erros = {};

  if (!exibirSelecaoMesAMes(state)) {
    return null;
  }

  const dataInicialCompostaSelecionada: ?string = getDataInicialCompostaSelecionada(
    state
  );
  const dataFinalCompostaSelecionada: ?string = getDataFinalCompostaSelecionada(
    state
  );
  const { translate } = i18n(state);

  if (!dataInicialCompostaSelecionada) {
    erros.dataInicial = translate("erro.dataInicialObrigatoria");
  }
  if (!dataFinalCompostaSelecionada) {
    erros.dataInicial = translate("erro.dataFinalObrigatoria");
  }
  if (dataInicialCompostaSelecionada && dataFinalCompostaSelecionada) {
    const momentInicial = moment(
      dataInicialCompostaSelecionada,
      FORMATO_DATA_MES_ANO_COMPOSTA,
      "pt-br",
      true
    );
    const momentFinal = moment(
      dataFinalCompostaSelecionada,
      FORMATO_DATA_MES_ANO_COMPOSTA,
      "pt-br",
      true
    );
    if (momentInicial.isAfter(momentFinal)) {
      erros.dataInicialInvalida = translate("erro.dataInicialMenorDataFinal");
    }
    if (momentFinal.isBefore(momentInicial)) {
      erros.dataInicialInvalida = translate("erro.dataInicialMenorDataFinal");
    }
  }
  return Object.keys(erros).length === 0 ? null : erros;
}

export const processarPeriodoMesAMes: ProcessadorPeriodo = (
  periodo: Id,
  state: State,
  medidasSelecionadas: Array<MedidaSelecionada>
) => {
  const edicaoMedida: EdicaoMedida = getEdicaoMedida(state);
  const modoExibicaoMedidaSelecionada:
    | ?ChaveEnum
    | ?Array<ChaveEnum> = getModoExibicaoMedidaSelecionada(state);
  const periodicidadeSelecionada: ?Id = getPeriodicidadeSelecionada(state);
  const mesesEAnoCompostos: Array<MesEAnoComposto> = getMesesEAnosCompostos(
    state
  );

  const indiceDataInicialComposta: number = mesesEAnoCompostos.findIndex(
    mesAno => mesAno.nome === edicaoMedida.dataInicialComposta
  );
  const indiceDataFinalComposta: number = mesesEAnoCompostos.findIndex(
    mesAno => mesAno.nome === edicaoMedida.dataFinalComposta
  );

  const medidasProcessadas: Array<MedidaSelecionada> = range(
    indiceDataFinalComposta,
    indiceDataInicialComposta + 1
  )
    .reverse()
    .map(indiceMesAnoComposto => mesesEAnoCompostos[indiceMesAnoComposto].nome)
    .flatMap(mes => {
      const idGrupo: Id = randomId();
      return medidasSelecionadas.map(medidaSelecionada => ({
        ...medidaSelecionada,
        idGrupoMedidasSelecionadas: idGrupo,
        periodo,
        dataInicialComposta: mes,
        periodicidade:
          // Esse modo de exibição é o único que tem o período mês a mês mas não tem o seletor de periodicidade.
          // Daí como o default é periodicidade diária, é preciso mudar pra mensal nesse ponto.
          modoExibicaoMedidaSelecionada === MODO_EXIBICAO_PONTUAL_MENSAL
            ? ID_PERIODICIDADE_MENSAL
            : periodicidadeSelecionada,
      }));
    });

  return medidasProcessadas;
};

export default connect(
  (state: State) => ({
    exibir: exibirSelecaoMesAMes(state),
    dataInicialCompostaSelecionada: getDataInicialCompostaSelecionada(state),
    dataFinalCompostaSelecionada: getDataFinalCompostaSelecionada(state),
    ajusteDiasSelecionado: getAjusteDiasSelecionado(state),
  }),
  {
    selecionarDataInicialComposta,
    selecionarDataFinalComposta,
    selecionarPrimeiraDataInicialComposta,
    selecionarPrimeiraDataFinalComposta,
  }
)(SelecaoMesAMes);
